import React, {useCallback, useEffect} from "react";
import PropTypes from "prop-types";
import moment from 'moment';
import useStarfishAPI from "../hooks/useStarfishAPI";
import Notification from "./Notification";
import css from "./Notifications.css";
import LoadingScreen from "./LoadingScreen";
import ErrorScreen from "./ErrorScreen";
import useDashboardError from "./useDashboardError";
import {useRefreshContext} from "./RefreshProvider";
import {RequestHelp} from "utils/types";

const notificationsURL = `../student/getInterventionSummary.html`;

/**
 * The container for a student's notifications
 *
 * @param {object} props - props for the component (defined with PropTypes below)
 * @returns {React.Component} a React component containing a collection of notification components.
 */
const Notifications = (props) => {
  const [response, callAPI] = useStarfishAPI();
  const responseValid = !response.pending && response.success && response.data && response.data.items;
  const itemsCount = (responseValid && response.data.items.length) || 0;
  const { refreshID } = useRefreshContext();

  const fetchAgenda = useCallback(() => {
    callAPI(notificationsURL, {
      method: "GET",
      headers: {
        Pragma: "no-cache",
        "Cache-Control": "no-cache",
        Expires: "-1"
      }
    });
  }, []);

  useEffect(() => {
    fetchAgenda();
  }, [fetchAgenda, refreshID]);

  useEffect(() => {
    props.setCount(itemsCount)
  }, [itemsCount]);

  const containerClasses = [css.container];
  if ( itemsCount ) containerClasses.push(css.border);

  useDashboardError(response, props.setError)

  const getContent = () => {
    if (response.pending) return <LoadingScreen/>
    if (response.error) return <ErrorScreen/>
    if (!responseValid) return "Sorry, we can't get notifications right now, try again later."
    if (!response.data.items.length) return 'Nice work! You have no active notifications at this time.'
    return response.data.items
      .sort((itemA, itemB) => {
        // Raised date sort is second priority
        return moment(itemA.raisedDate) - moment(itemB.raisedDate);
      })
      .sort((itemA, itemB) => {
        // Due date sort is first priority
        if (itemA.dueDate && itemB.dueDate) return moment(itemA.dueDate) - moment(itemB.dueDate)
        else if (itemA.dueDate && !itemB.dueDate) return -1
        else if (!itemA.dueDate && itemB.dueDate) return 1
        else return 0
      })
      .map((item, index) => <Notification {...item}
                                          key={index}
                                          studentId={props.studentId}
                                          successToastMessage={props.successToastMessage}
                                          requestHelp={props.requestHelp}/>)
  }

  return (
    <div className={containerClasses.join(" ")} data-testid='notifications'>
      { getContent() }
    </div>
  )
};

Notifications.propTypes = {
  setCount: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  studentId: PropTypes.number.isRequired,
  successToastMessage: PropTypes.func.isRequired,
  requestHelp: RequestHelp.isRequired
};

export default Notifications;
